import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import TopicCard from "@/components/feature/TopicCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories, topics } from "@/mocks/topics";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<"heat" | "new">("heat");

  usePageMeta({
    title: "话题广场 - 17吃瓜社区 | 影视综艺舞台热点全在这",
    description:
      "17吃瓜社区话题广场汇集影视热议、综艺现场、舞台演出、网络热点等分类讨论。按主题筛选或搜索关键词,快速找到你感兴趣的吃瓜话题并参与评论互动。",
    keywords: "17.cn话题广场,吃瓜话题,影视热议,综艺现场,网络热点,17.cn吃瓜圈子",
    path: "/topics",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "话题广场 - 17吃瓜社区",
      description: "17吃瓜社区的话题分类与讨论列表。",
      url: "https://17.cn/topics",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17.cn/" },
          { "@type": "ListItem", position: 2, name: "话题广场", item: "https://17.cn/topics" },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: topics.map((t, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: t.title,
          url: `https://17.cn/topic/${t.id}`,
        })),
      },
    },
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = topics.filter((t) => {
      const matchCategory = activeCategory === "all" || t.categoryId === activeCategory;
      const matchKeyword =
        !kw ||
        t.title.toLowerCase().includes(kw) ||
        t.summary.toLowerCase().includes(kw) ||
        t.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
    return list.sort((a, b) => (sort === "heat" ? b.heat - a.heat : Number(a.id) - Number(b.id)));
  }, [activeCategory, keyword, sort]);

  function selectCategory(id: string) {
    if (id === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", id);
    }
    setSearchParams(searchParams, { replace: true });
  }

  const activeName = categories.find((c) => c.id === activeCategory)?.name ?? "全部话题";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="px-4 md:px-10 pt-10 md:pt-14 pb-8">
          <nav aria-label="面包屑" className="text-xs md:text-sm text-foreground-500 mb-4">
            <ol className="flex items-center gap-2 flex-wrap">
              <li><Link to="/" className="hover:text-primary-700 transition-colors cursor-pointer">首页</Link></li>
              <li aria-hidden="true" className="text-foreground-300">/</li>
              <li className="text-foreground-800">{activeName}</li>
            </ol>
          </nav>
          <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-950">话题广场</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl leading-relaxed">
            社区里正在被讨论的话题都在这里。按分类筛选,或者直接搜关键词,找到你感兴趣的那一桌坐下来聊。
          </p>
        </section>

        <section className="px-4 md:px-10 pb-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="flex items-center gap-1 overflow-x-auto pb-1" role="tablist" aria-label="话题分类">
              {categories.map((c) => {
                const isActive = c.id === activeCategory;
                return (
                  <button
                    key={c.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => selectCategory(c.id)}
                    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      isActive ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${c.icon} mr-1.5`}></i>
                    {c.name}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <label className="relative flex-1 lg:w-64">
                <span className="sr-only">搜索话题</span>
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索话题或标签"
                  className="w-full pl-9 pr-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </label>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
                {(["heat", "new"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSort(s)}
                    className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      sort === s ? "bg-background-50 text-primary-700 border border-primary-200" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {s === "heat" ? "最热" : "最新"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-10 pb-20 md:pb-24">
          {filtered.length > 0 ? (
            <>
              <p className="text-xs md:text-sm text-foreground-500 mb-5">
                共找到 <span className="text-primary-700 font-medium">{filtered.length}</span> 条讨论
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((t) => (
                  <TopicCard key={t.id} topic={t} />
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center rounded-lg bg-background-100 border border-background-200/80">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-4">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="font-heading font-semibold text-lg text-foreground-900">没有找到相关话题</h2>
              <p className="text-sm text-foreground-500 mt-2">换个关键词试试,或者看看全部话题。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  selectCategory("all");
                }}
                className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}